/**
 * Unified domain search dispatcher — migrated from public/js/domain-search-universal.js.
 *
 * Form-uri trigger-uite prin `data-template`:
 *   "register-full"     → /inregistrare-domenii (sr-main/sr-suggest/sr-tlds)
 *   "tld-inline"        → /domeniu-X (.st-0/1/2/3)
 *   "redirect-fallback" → / homepage (no inline UI; redirect cu fqdn)
 *
 * Endpoint: GET /domain-search?domain=X — JSON cu {domain, available, price,
 * currency, error, alternatives: [{tld, available, price}]}.
 *
 * Submit interceptat pe CAPTURE phase + stopImmediatePropagation pentru a
 * preveni handler-ul global. AbortController per submit cancelează request
 * previous.
 *
 * NU foloseste Resort framework — direct DOM API. Modul ESM standalone.
 */

const AJAX_URL = '/domain-search';

interface SearchData {
    domain?:       string;
    available?:    boolean;
    price?:        number | string | null;
    currency?:     string;
    error?:        string;
    alternatives?: Array<{ tld: string; available: boolean; price: number | string }>;
}

interface SearchResult {
    ok:     boolean;
    status: number;
    data:   SearchData;
}

interface LoaderHandle {
    output:  HTMLElement;
    parent:  Element;
    overlay: HTMLElement;
}

interface FormWithAbort extends HTMLFormElement {
    _currentAbort?: AbortController;
}

/* ----------------------- Shared helpers ----------------------- */

function getTemplate(id: string): string {
    const tpl = document.getElementById(id);
    return tpl ? (tpl.textContent ?? '') : '';
}

function fillTemplate(str: string, vars: Record<string, unknown>): string {
    return str.replace(/\{\{(\w+)\}\}/g, (_, key: string) => vars[key] !== undefined ? String(vars[key]) : '');
}

const HTML_ESCAPE_MAP: Record<string, string> = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
function escapeHtml(str: unknown): string {
    return String(str == null ? '' : str).replace(/[&<>"']/g, (c) => HTML_ESCAPE_MAP[c] ?? c);
}

function formatPrice(p: unknown): string {
    if (p === null || p === undefined) return '0.00';
    const n = parseFloat(String(p));
    if (isNaN(n)) return '0.00';
    return n.toFixed(2);
}

function cartBase(form: HTMLFormElement): string {
    return form.dataset['cartUrl'] || 'https://client.ro/cart.php';
}

// client.ro language= for the visitor (data-cart-lang, set server-side from the
// host locale) — without it nav.at/nav.co visitors land in a Romanian cart.
function cartLang(form: HTMLFormElement): string {
    return form.dataset['cartLang'] || '';
}

function buildOrderUrl(base: string, fqdn: string, type: 'register' | 'transfer', lang: string): string {
    const url = `${base}?a=add&domain=${type === 'transfer' ? 'transfer' : 'register'}&query=${encodeURIComponent(fqdn)}`;
    return lang ? `${url}&language=${encodeURIComponent(lang)}` : url;
}

function stripTld(domain: string): string | null {
    if (!domain) return null;
    const i = domain.indexOf('.');
    return i > 0 ? domain.substring(0, i) : null;
}

/* ----------------------- Loader overlay ----------------------- */

function showLoader(output: HTMLElement): LoaderHandle | null {
    if (!output) return null;
    const parent = output.parentNode as HTMLElement | null;
    if (!parent) return null;

    const parentPos = parent.style.position || getComputedStyle(parent).position;
    if (parentPos === 'static' || parentPos === '') {
        parent.dataset['_loaderPrevPos'] = parent.style.position || '';
        parent.style.position = 'relative';
    }

    output.dataset['_loaderPrevDisplay'] = output.style.display || '';
    output.style.display = 'none';

    const overlay = document.createElement('div');
    overlay.className = 'sp-loader-overlay';
    overlay.setAttribute('role', 'status');
    overlay.setAttribute('aria-live', 'polite');
    overlay.setAttribute('aria-label', 'Loading');
    overlay.style.position = 'relative';
    overlay.style.minHeight = '160px';
    overlay.innerHTML = '<div class="nav-loader-wrap"><div class="nav-loader"></div></div>';
    parent.insertBefore(overlay, output);

    return { output, parent, overlay };
}

function hideLoader(handle: LoaderHandle | null): void {
    if (!handle) return;
    handle.overlay.parentNode?.removeChild(handle.overlay);
    if (handle.output.dataset['_loaderPrevDisplay'] !== undefined) {
        handle.output.style.display = handle.output.dataset['_loaderPrevDisplay']!;
        delete handle.output.dataset['_loaderPrevDisplay'];
    }
    if ((handle.parent as HTMLElement).dataset['_loaderPrevPos'] !== undefined) {
        (handle.parent as HTMLElement).style.position = (handle.parent as HTMLElement).dataset['_loaderPrevPos']!;
        delete (handle.parent as HTMLElement).dataset['_loaderPrevPos'];
    }
}

/* ----------------------- AJAX wrapper ----------------------- */

function ajaxSearch(
    form: HTMLFormElement,
    fqdn: string,
    loaderTarget: HTMLElement,
    abortSignal: AbortSignal,
    onResult: (out: SearchResult) => void,
): void {
    const btn = form.querySelector('button[type="submit"]') as HTMLButtonElement | null;
    const prevHtml = btn?.innerHTML ?? '';
    if (btn) { btn.disabled = true; btn.innerHTML = '<span>…</span>'; }
    const loaderHandle = showLoader(loaderTarget);

    fetch(`${AJAX_URL}?domain=${encodeURIComponent(fqdn)}`, {
        credentials: 'same-origin',
        headers:     { Accept: 'application/json' },
        signal:      abortSignal,
    })
        .then((r) => r.json().then((j) => ({ ok: r.ok, status: r.status, data: j as SearchData })))
        .catch((err: Error) => {
            if (err.name === 'AbortError') return null;
            return { ok: false, status: 0, data: { error: 'network' } } as SearchResult;
        })
        .then((out) => {
            if (out === null) return;
            hideLoader(loaderHandle);
            if (btn) { btn.disabled = false; btn.innerHTML = prevHtml; }
            requestAnimationFrame(() => onResult(out));
        });
}

function captureSubmit(form: FormWithAbort, handler: (e: Event, signal: AbortSignal) => void): void {
    form.addEventListener('submit', (e: Event) => {
        e.preventDefault();
        e.stopImmediatePropagation();
        if (form._currentAbort) form._currentAbort.abort();
        const controller = new AbortController();
        form._currentAbort = controller;
        handler(e, controller.signal);
    }, true);
}

/* ----------------------- Template: register-full ----------------------- */

function wireRegisterFull(form: HTMLFormElement): void {
    const input     = form.querySelector('input[name="fqdn"]') as HTMLInputElement | null;
    const modeInput = form.querySelector('input[name="mode"]') as HTMLInputElement | null;
    const output    = document.querySelector('.search-results') as HTMLElement | null;
    if (!input || !modeInput || !output) return;

    const base = cartBase(form);
    const lang = cartLang(form);

    captureSubmit(form as FormWithAbort, (_e, signal) => {
        const fqdn = (input.value || '').trim().toLowerCase();
        if (!fqdn) return;
        const mode = (modeInput.value || '1').trim();
        output.innerHTML = '';
        ajaxSearch(form, fqdn, output, signal, (out) => renderRegister(out, fqdn, mode));
    });

    function renderRegister(out: SearchResult, fqdn: string, mode: string): void {
        const data = out.data ?? {};
        output!.innerHTML = '';

        if (out.ok === false || data.error) {
            const errMsg = typeof data.error === 'string' ? data.error : 'Eroare la căutare. Reîncearcă.';
            output!.innerHTML = `<div class="sr-main"><p>${escapeHtml(errMsg)}</p></div>`;
            return;
        }

        const orderUrl    = buildOrderUrl(base, fqdn, 'register', lang);
        const transferUrl = buildOrderUrl(base, fqdn, 'transfer', lang);

        if (data.available === true && mode === '1') {
            output!.insertAdjacentHTML('beforeend', fillTemplate(getTemplate('tpl-sr-suggest'), {
                domain:   escapeHtml(fqdn),
                price:    formatPrice(data.price),
                orderUrl: escapeHtml(orderUrl),
            }));
        } else {
            let statusText: string;
            if (mode === '2') {
                statusText = data.available ? 'nu este înregistrat' : 'este înregistrat';
            } else {
                statusText = 'nu este disponibil';
            }
            output!.insertAdjacentHTML('beforeend', fillTemplate(getTemplate('tpl-sr-main'), {
                domain:      escapeHtml(fqdn),
                statusText:  escapeHtml(statusText),
                orderUrl:    escapeHtml(orderUrl),
                transferUrl: escapeHtml(transferUrl),
            }));
        }

        const baseLabel = stripTld(fqdn);
        const alternatives = (data.alternatives ?? []).slice(0, 7);
        if (baseLabel && alternatives.length > 0) {
            let tldsHtml = '';
            alternatives.forEach((alt) => {
                const candidate = baseLabel + alt.tld;
                tldsHtml += fillTemplate(getTemplate('tpl-sr-tld'), {
                    statusClass: alt.available ? 'st-1' : 'st-0',
                    tld:         escapeHtml(candidate),
                    price:       formatPrice(alt.price),
                    orderUrl:    escapeHtml(buildOrderUrl(base, candidate, 'register', lang)),
                });
            });
            output!.insertAdjacentHTML('beforeend', fillTemplate(getTemplate('tpl-sr-tlds-wrap'), { tlds: tldsHtml }));
        }
    }

    const switchBtns = document.querySelectorAll('.layer-1 .switch-btn a');
    if (switchBtns.length) {
        const isEn = /\/en\//.test(window.location.pathname) || document.documentElement.lang === 'en';
        switchBtns.forEach((a) => {
            a.addEventListener('click', function (this: Element, e: Event) {
                e.preventDefault();
                if (this.classList.contains('active')) return;
                switchBtns.forEach((el) => el.classList.remove('active'));
                this.classList.add('active');
                const newMode = this.getAttribute('data-id') || '1';
                modeInput.value = newMode;
                if (newMode === '1') {
                    input.setAttribute('placeholder', isEn ? 'Find your perfect domain name' : 'Caută numele de domeniu perfect pentru tine');
                    const sp = form.querySelector('button[type="submit"] span') as HTMLElement | null;
                    if (sp) sp.textContent = isEn ? 'CHECK' : 'VERIFICĂ';
                } else {
                    input.setAttribute('placeholder', isEn ? 'Add domain to transfer' : 'Adaugă domeniul pentru transfer');
                    const sp = form.querySelector('button[type="submit"] span') as HTMLElement | null;
                    if (sp) sp.textContent = 'GO';
                }
                output.innerHTML = '';
            });
        });
    }
}

/* ----------------------- Template: tld-inline ----------------------- */

function wireTldInline(form: HTMLFormElement): void {
    const input     = form.querySelector('input[name="fqdn"]') as HTMLInputElement | null;
    const modeInput = form.querySelector('input[name="mode"]') as HTMLInputElement | null;
    const output    = document.querySelector('header.domain .search-result') as HTMLElement | null;
    if (!input || !modeInput || !output) return;

    const base     = cartBase(form);
    const lang     = cartLang(form);
    const tldPrice = form.dataset['tldPrice'] || '0.00';

    captureSubmit(form as FormWithAbort, (_e, signal) => {
        const fqdn = (input.value || '').trim().toLowerCase();
        if (!fqdn) return;
        const mode = (modeInput.value || '1').trim();
        output.innerHTML = '';
        ajaxSearch(form, fqdn, output, signal, (out) => renderTld(out, fqdn, mode));
    });

    function renderTld(out: SearchResult, fqdn: string, mode: string): void {
        const data = out.data ?? {};
        output!.innerHTML = '';

        if (out.ok === false || data.error) {
            const errMsg = typeof data.error === 'string' ? data.error : 'Eroare la căutare. Reîncearcă.';
            output!.innerHTML = `<div class="dom-status st-0"><p class="rsp">${escapeHtml(errMsg)}</p></div>`;
            return;
        }

        let statusN: string;
        if (data.available === true) statusN = mode === '2' ? '2' : '1';
        else                          statusN = mode === '2' ? '3' : '0';

        const price = (data.price !== null && data.price !== undefined) ? formatPrice(data.price) : tldPrice;
        const url   = buildOrderUrl(base, fqdn, mode === '2' ? 'transfer' : 'register', lang);

        output!.insertAdjacentHTML('beforeend', fillTemplate(getTemplate(`tpl-tld-st${statusN}`), {
            domain: escapeHtml(fqdn),
            price,
            url:    escapeHtml(url),
        }));
    }

    const switchBtns = document.querySelectorAll('header.domain .switch-btn a');
    if (switchBtns.length) {
        switchBtns.forEach((a) => {
            a.addEventListener('click', function (this: Element, e: Event) {
                e.preventDefault();
                if (this.classList.contains('active')) return;
                switchBtns.forEach((el) => el.classList.remove('active'));
                this.classList.add('active');
                modeInput.value = this.getAttribute('data-id') || '1';
                output.innerHTML = '';
            });
        });
    }
}

/* ----------------------- Template: redirect-fallback ----------------------- */

function wireRedirectFallback(form: HTMLFormElement): void {
    const input = form.querySelector('input[name="fqdn"]') as HTMLInputElement | null;
    if (!input) return;
    const target = form.dataset['redirectUrl'] || '/inregistrare-domenii';

    captureSubmit(form as FormWithAbort, () => {
        const fqdn = (input.value || '').trim();
        if (!fqdn) return;
        const sep = target.indexOf('?') !== -1 ? '&' : '?';
        window.location.href = `${target}${sep}fqdn=${encodeURIComponent(fqdn)}`;
    });
}

/* ----------------------- Dispatch ----------------------- */

document.querySelectorAll<HTMLFormElement>('form[data-template]').forEach((form) => {
    const template = form.getAttribute('data-template') || '';
    switch (template) {
        case 'register-full':     wireRegisterFull(form);     break;
        case 'tld-inline':        wireTldInline(form);        break;
        case 'redirect-fallback': wireRedirectFallback(form); break;
        default:
            if (import.meta.env.DEV) console.warn('[domain-search] unknown data-template:', template, form);
    }
});
